import { createContext, useContext, type ReactNode } from 'react';
import { useOfflineQueue } from '../hooks/useOfflineQueue';
import type { FlushResult, OfflineQueueAction } from '../core/offline/queue';
import type { IDataAdapter } from '../adapters/data/IDataAdapter';

interface OfflineContextValue {
  online: boolean;
  enqueue: (action: { type: string; payload?: unknown; clientId?: string }) => string | null;
  queueSize: number;
  subscribeFlush: (listener: (results: FlushResult[]) => void) => () => void;
  readQueue: () => OfflineQueueAction[];
}

const OfflineCtx = createContext<OfflineContextValue | null>(null);

export function OfflineProvider({
  adapter,
  online,
  children,
}: {
  adapter: Pick<IDataAdapter, 'flush'> | null;
  online: boolean;
  children: ReactNode;
}) {
  const { enqueue, queueSize, subscribeFlush, readQueue } = useOfflineQueue({ adapter, online });

  return (
    <OfflineCtx.Provider value={{ online, enqueue, queueSize, subscribeFlush, readQueue }}>
      {children}
    </OfflineCtx.Provider>
  );
}

export function useOffline(): OfflineContextValue {
  const ctx = useContext(OfflineCtx);
  if (!ctx) throw new Error('useOffline must be used within OfflineProvider');
  return ctx;
}
